import { NotificationsService } from '@services/notifications/notifications.service';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthService } from '@services/auth';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	Output,
	ViewChild,
	computed,
	effect,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslationService } from '@services/translation.service';
import { NotificationsSidebarStatus } from '@enums/notifications';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Routing } from '@enums/routing.enum';
import { NavItem, navItemMatches, navItemsForRole } from '../nav.config';
import { canAccessModule } from '../module-access.config';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-cms-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.less'],
	standalone: true,
	imports: [RouterLink, MatIcon, TranslateModule],
})
export class HeaderComponent implements AfterViewInit {
	private readonly _ts = inject(TranslationService);
	private readonly _ns = inject(NotificationsService);
	private readonly _router = inject(Router);
	private readonly _destroyRef = inject(DestroyRef);
	private readonly _as = inject(AuthService);

	@Output() menuToggle = new EventEmitter<void>();

	/**
	 * Full destination list — the SAME shared config the desktop sidebar uses
	 * (nav.config.ts), minus admin-gated entries (admin has its own drawer tile).
	 * Rendered both as the swipeable pill AND the More-drawer grid, so every tab
	 * that exists on desktop is automatically reachable on mobile too.
	 */
	readonly moreLinks = computed(() => navItemsForRole(this._as.user()?.role));

	/** All destinations, shown as a single swipeable pill of icon buttons. */
	get allNav(): NavItem[] {
		return this.moreLinks();
	}

	@ViewChild('navTrack', { static: false }) navTrackEl?: ElementRef<HTMLDivElement>;
	readonly canNavLeft = signal(false);
	readonly canNavRight = signal(false);

	/** Czy jesteśmy na zakładce „Menu” — wtedy podświetlona jest ona, a nie moduł. */
	readonly naMenu = signal(false);
	readonly menuPath = '/' + Routing.PanelMenu;

	get isAdmin(): boolean {
		return this._as.user()?.role === 'admin';
	}

	/**
	 * A gated tab the user's tier can't access (admins never locked). Locked tabs
	 * stay visible but dimmed with a lock badge; `moduleGuard` intercepts the click
	 * and routes to the upsell page.
	 */
	isLocked(item: NavItem): boolean {
		if (this.isAdmin) {
			return false;
		}

		return !canAccessModule(this._as.user(), item.module);
	}

	readonly activeIndex = signal(0);
	/** CSS percentage left offset of the floating active highlight. */
	readonly indicatorPercent = computed(
		() => `${this.activeIndex() * (100 / this.allNav.length)}%`,
	);

	currentLanguage: string;
	languages: string[] = [];
	/** Global UI language signal (PL/EN) — reactive source for the nav switcher. */
	readonly currentLang = this._ts.currentLanguage;

	constructor() {
		effect(() => {
			this.currentLanguage = this._ts.currentLanguage();
		});
		effect(() => {
			this.languages = this._ts.languages();
		});

		this._syncActive(this._router.url);
		this._router.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				takeUntilDestroyed(this._destroyRef),
			)
			.subscribe((e) => this._syncActive(e.urlAfterRedirects || e.url));
	}

	private _syncActive(url: string): void {
		const menu = url.split(/[?#]/)[0] === this.menuPath;
		this.naMenu.set(menu);

		if (menu) {
			this.activeIndex.set(-1);
			return;
		}

		const idx = this.allNav.findIndex((n) => navItemMatches(n, url));

		if (idx >= 0 && idx !== this.activeIndex()) {
			this.activeIndex.set(idx);
			setTimeout(() => this._scrollActiveIntoView(), 0);
		}
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this._updateNavArrows();
			this._scrollActiveIntoView();
		}, 0);
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		this._updateNavArrows();
	}

	// ── Pill nav: edge arrows + swipe (native scroll) ─────────────────────────
	onNavScroll(): void {
		this._updateNavArrows();
	}

	scrollNav(dir: number): void {
		const el = this.navTrackEl?.nativeElement;

		if (!el) {
			return;
		}

		el.scrollBy({ left: dir * Math.max(140, el.clientWidth * 0.6), behavior: 'smooth' });
	}

	private _scrollActiveIntoView(): void {
		const el = this.navTrackEl?.nativeElement;

		if (!el) {
			return;
		}

		const active = el.querySelector<HTMLElement>('.navpill__item.active');

		if (active) {
			const target = active.offsetLeft - el.clientWidth / 2 + active.offsetWidth / 2;
			el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
		}

		this._updateNavArrows();
	}

	private _updateNavArrows(): void {
		const el = this.navTrackEl?.nativeElement;

		if (!el) {
			return;
		}

		const left = el.scrollLeft > 4;
		const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;

		if (left !== this.canNavLeft()) {
			this.canNavLeft.set(left);
		}

		if (right !== this.canNavRight()) {
			this.canNavRight.set(right);
		}
	}

	onMenuElementClick(): void {
		this._ns.notificationsSidebarStatus.set(NotificationsSidebarStatus.Close);
	}

	onMenuToggle(): void {
		this.menuToggle.emit();
	}

	onNavClick(index: number): void {
		this.activeIndex.set(index);
		this.onMenuElementClick();
	}
}
